import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { GoArrowUpRight } from 'react-icons/go';
import { IoArrowUpCircleOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import {
  ConversationDataFragment,
  ConversationType,
  DtType,
  MessageDataFragment,
  useSendAiMessageMutation,
  useGetAiMessageResponseMutation,
  useProfileDropdownQuery,
  ProfileDropdownDocument,
  useMarkConversationViewedMutation,
} from '../../generated/graphql';
import { getIndustryDetails } from '../../utils/industryDetails';
import { HelivanBackButton } from '../helivan/HelivanBackButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import ChatBubble from './ChatBubble';
import { ChatIndicatorBubble } from './ChatIndicatorBubble';

export const DEFAULT_AVATAR_COLOR = '#F9D5FF';

export const ViewConversation = ({
  data,
}: {
  data: ConversationDataFragment;
}) => {
  const navigate = useNavigate();
  const industryDetails = getIndustryDetails(
    data.digitalTwins ? data.digitalTwins[0].type : DtType.Congressperson
  );
  const [industryHovered, setIndustryHovered] = useState(false);
  const [activeMessage, setActiveMessage] = useState('');
  const [messages, setMessages] = useState<MessageDataFragment[]>(
    data.messages || []
  );
  const [sendMessage, { loading: sendMessageLoading }] =
    useSendAiMessageMutation();
  const [getResponse, { loading: getResponseLoading }] =
    useGetAiMessageResponseMutation();
  const [markConversationViewed] = useMarkConversationViewedMutation();
  const { data: userData } = useProfileDropdownQuery();
  const canSendMessage = userData?.user?.canSendMessage ?? true;

  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Mark conversation as viewed when component mounts
  useEffect(() => {
    markConversationViewed({
      variables: {
        id: data.id,
      },
    });
  }, [data.id, markConversationViewed]);

  const scrollToBottom = () => {
    // Get the current element
    const container = chatContainerRef.current;

    // Only proceed if the ref is attached to an element
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  // Scroll to bottom on initial render
  useEffect(() => {
    scrollToBottom();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Update messages when data changes
  useEffect(() => {
    if (data.messages) {
      setMessages(data.messages);
    }
  }, [data.messages]);

  const [dtToColor, setDtToColor] = useState<{ dtId: string; color: string }[]>(
    []
  );

  const getAvatarColor = (idx: number): string => {
    if (idx % 3 === 0) {
      return DEFAULT_AVATAR_COLOR;
    }
    if (idx % 3 === 1) {
      return '#E3F9B9';
    }
    return '#D9C3F6';
  };

  const getDtColor = (dtId: string) => {
    return (
      dtToColor.find((dt) => dt.dtId === dtId)?.color ?? DEFAULT_AVATAR_COLOR
    );
  };

  const handleSendMessage = () => {
    if (activeMessage.length === 0 || sendMessageLoading) return;

    sendMessage({
      variables: {
        message: activeMessage,
        conversationId: data.id,
      },
      refetchQueries: [{ query: ProfileDropdownDocument }],
      onCompleted: (responseData) => {
        setActiveMessage('');
        if (responseData.sendAIMessage.messages) {
          setMessages(responseData.sendAIMessage.messages);
        }
        getResponse({
          variables: {
            conversationId: data.id,
          },
          onCompleted: (responseData) => {
            if (responseData.getAIMessageResponse.messages) {
              setMessages(responseData.getAIMessageResponse.messages);
            }
          },
        });
      },
    });
  };

  useEffect(() => {
    if (data?.digitalTwins && data.digitalTwins.length > 0) {
      const colors: { dtId: string; color: string }[] = [];
      for (let i = 0; i < data.digitalTwins.length; i++) {
        colors.push({
          dtId: data.digitalTwins[i].id,
          color: getAvatarColor(i),
        });
      }
      setDtToColor(colors);
    }
  }, [data]);

  return (
    <Flex
      width="full"
      height="full"
      justifyContent="center"
      px="88px"
      py="22px"
      bg={HelivanColor.background.primary}
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      borderRadius="8px"
    >
      <Stack width="full" alignItems="center" textAlign="center" spacing="22px">
        <Flex width="full" justifyContent="space-between">
          <HelivanBackButton
            onClick={() => {
              navigate('/conversations');
            }}
          />

          {data.digitalTwins.length === 1 &&
            data.type === ConversationType.Persona && (
              <Flex
                p="6px 16px"
                borderRadius="full"
                alignItems="center"
                justifyContent="center"
                width="fit-content"
                bg="#0D152D"
                _hover={{ cursor: 'pointer', bg: '#486884' }}
                onClick={() => {
                  navigate(`/personas/${data.digitalTwins[0].id}`);
                }}
              >
                <HelivanText variant="largeBody+" colorOverride="white">
                  view persona
                </HelivanText>
              </Flex>
            )}
        </Flex>
        <HelivanText mt="-56px" variant="sectionHeader">
          {data.type === ConversationType.Demographic
            ? data.demographic
            : data.digitalTwins.map((dt) => dt.name).join(', ')}
        </HelivanText>
        {data.projectId && (
          <HStack
            spacing="6px"
            onClick={() => {
              navigate(`/projects/${data.projectId}`);
            }}
            onMouseEnter={() => setIndustryHovered(true)}
            onMouseLeave={() => setIndustryHovered(false)}
            cursor="pointer"
          >
            <Icon
              as={industryDetails.industryDetails.icon}
              width="24px"
              height="24px"
              color={HelivanColor.text.blueMid}
            />
            <HelivanText
              transition="text-decoration 0.2s ease"
              textDecoration={industryHovered ? 'underline' : 'none'}
              variant="largeSubheader"
            >
              {data.projectName}
            </HelivanText>
            <GoArrowUpRight
              style={{
                transform: industryHovered ? 'rotate(45deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
              size={24}
              color="black"
            />
          </HStack>
        )}
        <HelivanText variant="largeBody" colorOverride="black">
          {data.title}
        </HelivanText>
        <Flex
          borderRadius="12px"
          border="1px solid #D7DEE2"
          width="full"
          bg="white"
          p={8}
          height="full"
          overflowY="scroll"
          ref={chatContainerRef}
        >
          <Stack width="full" spacing={2}>
            {messages?.map((msg, idx) => {
              return (
                <HStack spacing={3} width="full" key={idx}>
                  {!msg.userSender && (
                    <Avatar
                      size="sm"
                      bg={
                        msg.digitalTwin?.id
                          ? getDtColor(msg.digitalTwin.id)
                          : undefined
                      }
                      name={msg.digitalTwin?.name}
                      color="black"
                      alignSelf="flex-start"
                      mt={data.type === ConversationType.Panel ? 6 : 2}
                    />
                  )}
                  <ChatBubble
                    content={msg.content}
                    senderUser={msg.userSender}
                    name={
                      data.type === ConversationType.Panel
                        ? msg.digitalTwin?.name
                        : undefined
                    }
                    retrievedContent={msg.retrievedContent ?? undefined}
                  />
                </HStack>
              );
            })}
            {getResponseLoading && <ChatIndicatorBubble />}
            <HelivanText variant="largeBody" colorOverride="transparent">
              space
            </HelivanText>
          </Stack>
        </Flex>
        <Tooltip
          label="You're out of free credits. Contact info@helivan.io to pay for more. Credits update every week."
          isDisabled={canSendMessage}
          placement="top"
        >
          <InputGroup>
            <Input
              bg="white"
              px={5}
              py={4}
              width="full"
              borderRadius="12px"
              height="50px"
              borderWidth="1px"
              borderColor="#D7DEE2"
              value={activeMessage}
              onChange={(e) => setActiveMessage(e.target.value)}
              placeholder={`Ask ${data.type === ConversationType.Persona ? 'me' : data.type === ConversationType.Demographic ? data.demographic : 'us'} about anything`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={sendMessageLoading || getResponseLoading || !canSendMessage}
            />
            <InputRightElement
              height="full"
              alignItems="center"
              onClick={
                sendMessageLoading || getResponseLoading || !canSendMessage
                  ? undefined
                  : handleSendMessage
              }
              cursor={
                sendMessageLoading || getResponseLoading || !canSendMessage
                  ? 'not-allowed'
                  : 'pointer'
              }
            >
              <Box>
                <IoArrowUpCircleOutline
                  size={30}
                  color={HelivanColor.text.blueDark}
                />
              </Box>
            </InputRightElement>
          </InputGroup>
        </Tooltip>
      </Stack>
    </Flex>
  );
};
